import { ReactNode } from 'react';

import { AutoComplete } from 'primereact/autocomplete';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import {
  Control,
  Controller,
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormSetValue,
  useFormState,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { type StringMapOption, useStringMap } from '@/hooks/useStringMap';
import { cn } from '@/lib/utils';
import {
  Account,
  SHIPPING_ACCOUNT_SEARCH,
  SHIPPING_EQUIPMENT_SEARCH,
  SHIPPING_SITE_SEARCH,
} from '@/types.gql';

import { useSearchQuery } from '../../hooks/useSearchQuery';
import {
  billStatusOptions,
  shipmentCategoryOptions,
  usStateOptions,
} from '../../utils/constants';
import { ShipmentSchema } from '../../utils/schema';
import {
  AccountField,
  FormField,
  NeedByDateField,
  OriginalNeedByDateField,
} from './FormField';

const EMPTY_OPTIONS: StringMapOption[] = [];

type BaseShipmentFormProps = {
  control: Control<ShipmentSchema>;
  handleSubmit: UseFormHandleSubmit<ShipmentSchema>;
  setValue: UseFormSetValue<ShipmentSchema>;
  watch: UseFormWatch<ShipmentSchema>;
  onSubmit: SubmitHandler<ShipmentSchema>;
  shipmentStatus: { options: any[] };
  shipmentInProgress?: boolean;
  additionalContent?: ReactNode;
};

export const BaseShipmentForm = ({
  control,
  handleSubmit,
  setValue,
  watch,
  onSubmit,
  shipmentStatus,
  shipmentInProgress = false,
  additionalContent,
}: BaseShipmentFormProps) => {
  const { t } = useTranslation();
  const { errors } = useFormState({ control });
  const isCallTagAlwaysEditable = useFeatureFlag('Shipping_Call_Tag');

  const {
    filteredResults: filteredEquipments,
    search: searchEquipment,
    setFilteredResults: setFilteredEquipments,
  } = useSearchQuery(SHIPPING_EQUIPMENT_SEARCH, 'results');
  const {
    filteredResults: filteredShipFromSites,
    search: searchShipFromSites,
    setFilteredResults: setFilteredShipFromSites,
  } = useSearchQuery(SHIPPING_SITE_SEARCH, 'results');
  const {
    filteredResults: filteredShipToSites,
    search: searchShipToSites,
    setFilteredResults: setFilteredShipToSites,
  } = useSearchQuery(SHIPPING_ACCOUNT_SEARCH, 'results');
  const {
    filteredResults: filteredAccounts,
    search: searchAccount,
    setFilteredResults: setFilteredAccounts,
  } = useSearchQuery(SHIPPING_ACCOUNT_SEARCH, 'results');

  const shippingMethodResult = useStringMap('shipping_method');
  const shippingMethodOptions =
    shippingMethodResult.type === 'success'
      ? shippingMethodResult.options
      : EMPTY_OPTIONS;

  const currentOwner = watch('owner') as Account | null;

  return (
    <form
      className="content-panel grid grid-cols-3 gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Fieldset
        legend={t('shipping.shipmentInformation')}
        className="col-span-2"
        pt={{ content: { className: 'grid grid-cols-2 gap-4' } }}
      >
        <AccountField
          control={control}
          setValue={setValue}
          value={watch('owner')}
          shipmentInProgress={shipmentInProgress}
          filteredAccounts={filteredAccounts}
          searchAccount={searchAccount}
          setFilteredAccounts={setFilteredAccounts}
          minLength={MIN_SEARCH_LENGTH}
          selectionLimit={SELECTION_LIMIT}
        />

        <NeedByDateField
          control={control}
          shipmentInProgress={shipmentInProgress}
        />

        <OriginalNeedByDateField
          control={control}
          shipmentInProgress={shipmentInProgress}
        />

        <FormField
          label={t('shipping.shipmentStatus')}
          name="shipmentStatus"
          control={control}
          component={Dropdown}
          props={{
            options: shipmentStatus.options,
            optionDisabled: 'disabled',
          }}
        />
        <FormField
          label={t('shipping.shipFromSite')}
          name="shipFromSite"
          control={control}
          setValue={setValue}
          component={AutoComplete}
          props={{
            field: 'name',
            suggestions: filteredShipFromSites,
            minLength: MIN_SEARCH_LENGTH,
            selectionLimit: SELECTION_LIMIT,
            completeMethod: searchShipFromSites,
            onDropdownClick: () => setFilteredShipFromSites(undefined),
            dropdown: true,
            value: watch('shipFromSite') ?? '',
            onChange: (e: any) => setValue('shipFromSite', e.value),
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipDate')}
          name="date"
          control={control}
          component={Calendar}
          props={{
            showIcon: true,
          }}
        />
      </Fieldset>

      <Fieldset
        legend={t('shipping.contact')}
        pt={{ content: { className: 'flex flex-col gap-4' } }}
      >
        <FormField
          label={t('shipping.requestedBy')}
          name="requestedBy"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.contactPhone')}
          name="phone"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.contactEmail')}
          name="email"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
      </Fieldset>

      <Fieldset
        legend={t('shipping.shipmentDestination')}
        pt={{ content: { className: 'flex flex-col gap-4' } }}
      >
        <FormField
          label={t('shipping.shipTo')}
          name="shipTo"
          control={control}
          setValue={setValue}
          component={AutoComplete}
          props={{
            field: 'name',
            suggestions: filteredShipToSites,
            minLength: MIN_SEARCH_LENGTH,
            selectionLimit: SELECTION_LIMIT,
            completeMethod: searchShipToSites,
            onDropdownClick: () => setFilteredShipToSites(undefined),
            dropdown: true,
            value: watch('shipTo') ?? '',
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipName')}
          name="shipName"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipAddress')}
          name="shipAddress"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipAddress2')}
          name="shipAddress2"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipAddress3')}
          name="shipAddress3"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipCity')}
          name="shipCity"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipState')}
          name="shipState"
          control={control}
          component={Dropdown}
          props={{
            options: usStateOptions,
            editable: true,
            filter: true,
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('shipping.shipZipCode')}
          name="shipZip"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
      </Fieldset>

      <div className="flex flex-col gap-4">
        <Fieldset
          legend={t('shipping.shippingDetails')}
          pt={{ content: { className: 'flex flex-col gap-4' } }}
        >
          <div className="label-input-group">
            <label htmlFor="amount" id="amount-label">
              {t('shipping.amount')}
            </label>
            <Controller
              name="amount"
              control={control}
              render={({ field, fieldState }) => (
                <InputNumber
                  id={field.name}
                  inputId={field.name}
                  aria-labelledby="amount-label"
                  name={field.name}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  value={field.value ?? null}
                  disabled={field.disabled}
                  onValueChange={(e: InputNumberValueChangeEvent) =>
                    field.onChange(e.value)
                  }
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  showButtons={false}
                  className={fieldState.invalid ? 'p-invalid' : ''}
                />
              )}
            />
          </div>

          <FormField
            label={t('shipping.tankIncubator')}
            name="tank"
            control={control}
            component={InputText}
          />

          <FormField
            label={t('shipping.shippingMethod')}
            name="shippingMethod"
            control={control}
            component={Dropdown}
            props={{
              options: shippingMethodOptions,
              disabled: shipmentInProgress,
            }}
          />

          <FormField
            label={t('shipping.trackingNumber')}
            name="trackingNumber"
            control={control}
            component={InputText}
          />
        </Fieldset>

        <Fieldset pt={{ content: { className: 'flex flex-col gap-4' } }}>
          <FormField
            label={t('shipping.callTag')}
            name="callTag"
            control={control}
            component={InputText}
            props={{
              disabled: shipmentInProgress && !isCallTagAlwaysEditable,
            }}
          />
          <FormField
            label={t('shipping.shipmentCategory')}
            name="shipmentCategory"
            control={control}
            component={Dropdown}
            props={{
              options: shipmentCategoryOptions,
              disabled: shipmentInProgress,
            }}
          />
        </Fieldset>

        <Fieldset pt={{ content: { className: 'flex flex-col gap-4' } }}>
          <FormField
            label={t('shipping.packedById')}
            name="packedBy"
            control={control}
            setValue={setValue}
            component={AutoComplete}
            props={{
              field: 'name',
              suggestions: filteredEquipments,
              minLength: MIN_SEARCH_LENGTH,
              selectionLimit: SELECTION_LIMIT,
              completeMethod: searchEquipment,
              onDropdownClick: () => setFilteredEquipments(undefined),
              dropdown: true,
              value: watch('packedBy') ?? '',
              onChange: (e: any) => setValue('packedBy', e.value),
            }}
          />
          {errors.packedById && (
            <small className="p-error">{errors.packedById.message}</small>
          )}
        </Fieldset>
      </div>

      <div className="flex flex-col gap-4">
        <Fieldset
          legend={t('shipping.contains')}
          pt={{ content: { className: 'flex flex-col gap-4' } }}
        >
          <div className="flex flex-row flex-wrap gap-2">
            <Controller
              name="hasEmbryos"
              control={control}
              disabled={shipmentInProgress}
              render={({ field, fieldState }) => (
                <>
                  <Checkbox
                    inputId={field.name}
                    invalid={fieldState.invalid}
                    inputRef={field.ref}
                    disabled={field.disabled}
                    name={field.name}
                    onBlur={field.onBlur}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />

                  <label
                    htmlFor={field.name}
                    className={cn(
                      fieldState.error?.message && 'text-destructive',
                    )}
                  >
                    {t('embryo.embryos')}
                  </label>

                  {fieldState.error?.message && (
                    <small className="text-destructive w-full flex-grow pl-8.5">
                      {fieldState.error?.message}
                    </small>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <Controller
              name="hasSemen"
              control={control}
              disabled={shipmentInProgress}
              render={({ field, fieldState }) => (
                <>
                  <Checkbox
                    inputId={field.name}
                    invalid={fieldState.invalid}
                    inputRef={field.ref}
                    disabled={field.disabled}
                    name={field.name}
                    onBlur={field.onBlur}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />

                  <label
                    htmlFor={field.name}
                    className={cn(
                      fieldState.error?.message && 'text-destructive',
                    )}
                  >
                    {t('semen.semen')}
                  </label>

                  {fieldState.error?.message && (
                    <small className="text-destructive w-full flex-grow pl-8.5">
                      {fieldState.error?.message}
                    </small>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <Controller
              name="hasDonors"
              control={control}
              disabled={shipmentInProgress}
              render={({ field, fieldState }) => (
                <>
                  <Checkbox
                    inputId={field.name}
                    invalid={fieldState.invalid}
                    inputRef={field.ref}
                    disabled={field.disabled}
                    name={field.name}
                    onBlur={field.onBlur}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />

                  <label
                    htmlFor={field.name}
                    className={cn(
                      fieldState.error?.message && 'text-destructive',
                    )}
                  >
                    {t('donor.donors')}
                  </label>

                  {fieldState.error?.message && (
                    <small className="text-destructive w-full flex-grow pl-8.5">
                      {fieldState.error?.message}
                    </small>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <Controller
              name="hasRecipients"
              control={control}
              disabled={shipmentInProgress}
              render={({ field, fieldState }) => (
                <>
                  <Checkbox
                    inputId={field.name}
                    invalid={fieldState.invalid}
                    inputRef={field.ref}
                    disabled={field.disabled}
                    name={field.name}
                    onBlur={field.onBlur}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />

                  <label
                    htmlFor={field.name}
                    className={cn(
                      fieldState.error?.message && 'text-destructive',
                    )}
                  >
                    {t('recipients.recipients')}
                  </label>

                  {fieldState.error?.message && (
                    <small className="text-destructive w-full flex-grow pl-8.5">
                      {fieldState.error?.message}
                    </small>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <Controller
              name="hasCalves"
              control={control}
              disabled={shipmentInProgress}
              render={({ field, fieldState }) => (
                <>
                  <Checkbox
                    inputId={field.name}
                    invalid={fieldState.invalid}
                    inputRef={field.ref}
                    disabled={field.disabled}
                    name={field.name}
                    onBlur={field.onBlur}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />

                  <label
                    htmlFor={field.name}
                    className={cn(
                      fieldState.error?.message && 'text-destructive',
                    )}
                  >
                    {t('calves.calves')}
                  </label>

                  {fieldState.error?.message && (
                    <small className="text-destructive w-full flex-grow pl-8.5">
                      {fieldState.error?.message}
                    </small>
                  )}
                </>
              )}
            />
          </div>
        </Fieldset>

        <Fieldset pt={{ content: { className: 'flex flex-col gap-4' } }}>
          <div className="label-input-group">
            <label htmlFor="aging30" id="aging30-label">
              {t('account.aging0to30')}
            </label>
            <div className="w-full">
              <InputNumber
                name="aging30"
                value={currentOwner?.aging30}
                disabled={true}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={2}
                showButtons={false}
              />
            </div>
          </div>

          <div className="label-input-group">
            <label htmlFor="aging60" id="aging60-label">
              {t('account.aging31to60')}
            </label>
            <div className="w-full">
              <InputNumber
                name="aging60"
                value={currentOwner?.aging60}
                disabled={true}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={2}
                showButtons={false}
              />
            </div>
          </div>

          <div className="label-input-group">
            <label htmlFor="aging90" id="aging90-label">
              {t('account.aging61to90')}
            </label>
            <div className="w-full">
              <InputNumber
                name="aging90"
                value={currentOwner?.aging90}
                disabled={true}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={2}
                showButtons={false}
              />
            </div>
          </div>

          <div className="label-input-group">
            <label htmlFor="aging120" id="aging120-label">
              {t('account.aging91to120')}
            </label>
            <div className="w-full">
              <InputNumber
                name="aging120"
                value={currentOwner?.aging120}
                disabled={true}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={2}
                showButtons={false}
              />
            </div>
          </div>

          <div className="label-input-group">
            <label htmlFor="aging180" id="aging180-label">
              {t('account.aging121to180')}
            </label>
            <div className="w-full">
              <InputNumber
                name="aging180"
                value={currentOwner?.aging180}
                disabled={true}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={2}
                showButtons={false}
              />
            </div>
          </div>

          <div className="label-input-group">
            <label htmlFor="aging365" id="aging365-label">
              {t('account.aging181to365')}
            </label>
            <div className="w-full">
              <InputNumber
                name="aging365"
                value={currentOwner?.aging365}
                disabled={true}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={2}
                showButtons={false}
              />
            </div>
          </div>

          <div className="label-input-group">
            <label htmlFor="aging365plus" id="aging365plus-label">
              {t('account.aging365plus')}
            </label>
            <div className="w-full">
              <InputNumber
                name="aging365plus"
                value={currentOwner?.aging365Plus}
                disabled={true}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={2}
                showButtons={false}
              />
            </div>
          </div>
        </Fieldset>
      </div>

      <Fieldset
        legend={t('billing.billing')}
        pt={{ content: { className: 'flex flex-col gap-4' } }}
      >
        <FormField
          label={t('billing.billTo')}
          name="billTo"
          control={control}
          component={InputText}
          props={{
            disabled: shipmentInProgress,
          }}
        />
        <FormField
          label={t('billing.billStatus')}
          name="billStatus"
          control={control}
          component={Dropdown}
          props={{
            options: billStatusOptions,
            disabled: shipmentInProgress,
          }}
        />
      </Fieldset>

      <Fieldset pt={{ content: { className: 'flex flex-col gap-4' } }}>
        <div className="label-input-group comment">
          <label htmlFor="comment" id="comment-label">
            {t('common.comment')}
          </label>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <InputTextarea
                id="comment"
                aria-labelledby="comment-label"
                {...field}
                value={field?.value ?? ''}
              />
            )}
          />
        </div>

        {additionalContent}
      </Fieldset>
    </form>
  );
};
